import { useState, useEffect } from 'react';
import { Container, Group, Button, TextInput } from '@mantine/core';
import { useSelector } from 'react-redux';
import {
  IProduct,
  IProductRequest,
  addProductDetails,
  updateProductDetails,
  deleteProduct
} from '../../../libraries/products';
import { AppState } from '../../client-redux/reducers';
import ModalComponent from '../../components/modal';
import { Language } from '../../languages/languageHandler';
import { ICurrency } from '../../../libraries/currencies';
import CurrencyInput from '../../components/currency-input';
import { StatusOper } from '../../../libraries/zps';
import StatusOperModal from '../../components/status-oper-modal';

interface AddEditProductProps {
  product?: IProduct;
  mode: 'new' | 'edit';
  opened: boolean;
  close: () => void;
  onSaved?: () => void;
}

const AddEditProduct = ({ product, mode, opened, close, onSaved }: AddEditProductProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currency = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = new AbortController();

  const [formData, setFormData] = useState<IProductRequest>({
    productid: product?.productid ?? '',
    name: product?.name || '',
    price: product?.price || 0
  });
  const [loading, setLoading] = useState(false);

  const [statusOper, setStatusOper] = useState<StatusOper | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [actionType, setActionType] = useState<'add' | 'edit' | 'delete'>('add');

  // Populate formData when product or mode changes
  useEffect(() => {
    if (product) {
      setFormData({
        productid: product.productid ?? '',
        name: product.name,
        price: product.price
      });
    } else {
      setFormData({ productid: '', name: '', price: 0 });
    }
  }, [product, mode]);

  const showStatus = (status: StatusOper) => {
    setStatusOper(status);
    setShowStatusModal(true);

    if (status.code === 0) {
      onSaved?.();

      setTimeout(() => {
        setShowStatusModal(false);
        close();
      }, 2000);
    }
  };

  // Update form field
  const handleChange = (field: keyof IProductRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save product (add or edit)
  const handleSave = async () => {
    setLoading(true);
    setActionType(mode === 'new' ? 'add' : 'edit');
    try {
      const response =
        mode === 'new'
          ? await addProductDetails(formData, controller)
          : await updateProductDetails(formData, controller);

      showStatus(response.statusOper);
    } catch (err: any) {
      const statusOper: StatusOper = err?.response?.data?.statusOper ?? {
        code: -1,
        message: err.message || 'An unexpected error occurred'
      };
      showStatus(statusOper);
    } finally {
      setLoading(false);
    }
  };

  //Delete product
  const handleDelete = async () => {
    if (!product) return;
    setLoading(true);
    setActionType('delete');
    try {
      const response = await deleteProduct(product.entid, product.productid);
      showStatus(response.statusOper);
    } catch (err: any) {
      const statusOper: StatusOper = err?.response?.data?.statusOper ?? {
        code: -1,
        message: err.message || 'An unexpected error occurred'
      };
      showStatus(statusOper);
    } finally {
      setLoading(false);
    }
  };

  const title = mode === 'new' ? lang.PRODUCTS_ADD : `${lang.PRODUCTS_EDIT} - ${product?.productid}`;

  return (
    <>
      <ModalComponent
        show={opened}
        handleClick={close}
        title={title}
        content={
          <Container className="product-details-modal">
            <Group className="product-details">
              <TextInput
                label={lang.PRODUCTS_ID}
                value={formData.productid}
                onChange={e => handleChange('productid', e.currentTarget.value)}
                disabled={mode !== 'new' || loading}
              />
              <TextInput
                label={lang.PRODUCTS_NAME}
                value={formData.name}
                onChange={e => handleChange('name', e.currentTarget.value)}
                disabled={loading}
              />
              <CurrencyInput
                value={formData.price}
                label={lang.PRODUCTS_PRICE}
                currencySymbol={currency[0]?.currencyStr}
                showSymbol
                disabled={loading}
                onChange={value => handleChange('price', value)}
              />
            </Group>

            <Group className="product-buttons">
              <Button radius="md" onClick={handleSave} disabled={loading}>
                {lang.GLOBAL_SAVE_BUTTON}
              </Button>
              {mode === 'edit' && (
                <Button radius="md" onClick={handleDelete} disabled={loading}>
                  {lang.GLOBAL_DELETE}
                </Button>
              )}
            </Group>
          </Container>
        }
      />

      {statusOper && (
        <StatusOperModal
          opened={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          statusOper={statusOper}
          successMessage={
            statusOper.code === 0
              ? actionType === 'add'
                ? lang.PRODUCTS_ADD_SUCCESS
                : actionType === 'edit'
                  ? lang.PRODUCTS_EDIT_SUCCESS
                  : lang.PRODUCTS_DELETE_SUCCESS
              : undefined
          }
          errorMessage={lang.PRODUCTS_ADD_ERROR}
        />
      )}
    </>
  );
};

export default AddEditProduct;
